import { useState, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Calculator } from './Calculator'
import { FeedPage } from './FeedPage'
import { ProfilePage } from './ProfilePage'
import { SettingsPage } from './SettingsPage'
import { PublicProfilePage } from './PublicProfilePage'
import { ErrorBoundary } from './ErrorBoundary'
import type { CipherResult } from '@gematria/shared'

export interface User {
  id: string
  username: string
}

export interface StoredPhrase {
  phrase: string
  results: CipherResult[]
  timestamp: number | string
  category?: string
  tags?: string[] | string  // Can be array or comma-separated string from backend
}

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [storedPhrases, setStoredPhrases] = useState<StoredPhrase[]>([])
  const [userCategories, setUserCategories] = useState<string[]>(() => {
    // Try to load from localStorage first
    const saved = localStorage.getItem('userCategories')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return ['uncategorized']
      }
    }
    return ['uncategorized']
  })

  // Save categories to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('userCategories', JSON.stringify(userCategories))
  }, [userCategories])

  useEffect(() => {
    const session = localStorage.getItem('session')
    const savedUser = localStorage.getItem('user')
    if (session && savedUser) {
      setUser(JSON.parse(savedUser))
    }
  }, [])

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <ErrorBoundary>
            <Calculator 
              user={user}
              setUser={setUser}
              storedPhrases={storedPhrases}
              setStoredPhrases={setStoredPhrases}
              userCategories={userCategories}
              setUserCategories={setUserCategories}
            />
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/feed" 
        element={
          <ErrorBoundary>
            <FeedPage user={user} />
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ErrorBoundary>
            <ProfilePage 
              user={user}
              userCategories={userCategories}
              setUserCategories={setUserCategories}
            />
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ErrorBoundary>
            <SettingsPage 
              user={user}
            />
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/u/:username" 
        element={
          <ErrorBoundary>
            <PublicProfilePage />
          </ErrorBoundary>
        }
      />
    </Routes>
  )
}

export default App
